import { useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { RingCentralConnection } from '@/types/ringcentral';

import { useAuthStore } from './auth';

export const useOrganizationStore = defineStore('organization', () => {
  // State - persist currentOrgId to localStorage to prevent losing selection
  const currentOrgId = useLocalStorage<string | null>('currentOrgId', null);
  const loading = ref(false);
  const ringCentralConnection = ref<RingCentralConnection | null>(null);

  // Business phone numbers per organization (identified by frequency analysis)
  // Key: orgId, Value: Set of business phone numbers
  const orgBusinessNumbers = ref<Map<string, Set<string>>>(new Map());

  // Track last used timestamp for each organization (persisted to localStorage)
  const lastUsedTimestamps = useLocalStorage<Record<string, number>>('orgLastUsedTimestamps', {});

  // Get auth store to access memberships
  const authStore = useAuthStore();

  // Getters with defensive checks
  const memberships = computed(() => {
    return authStore.memberships || [];
  });

  const currentOrganization = computed(() => {
    if (!currentOrgId.value) {
      return null;
    }
    // Add defensive check for memberships.value
    return memberships.value?.find(org => org?.orgId === currentOrgId.value) || null;
  });

  const hasOrganizations = computed(() => {
    // Add defensive check
    return Array.isArray(memberships.value) && memberships.value.length > 0;
  });

  // RingCentral computed properties
  const isRingCentralConnected = computed(() => {
    return ringCentralConnection.value?.status === 'connected';
  });

  const ringCentralStatus = computed(() => {
    return ringCentralConnection.value?.status || null;
  });

  const canManageRingCentral = computed(() => {
    if (!currentOrganization.value || !authStore.memberships) {return false;}

    return authStore.memberships.some(
      m => m.orgId === currentOrganization.value?.orgId && ['admin', 'owner'].includes(m.role),
    );
  });

  // Get business numbers for current organization
  const currentOrgBusinessNumbers = computed(() => {
    if (!currentOrgId.value) {return new Set<string>();}
    return orgBusinessNumbers.value.get(currentOrgId.value) || new Set<string>();
  });

  // Actions
  async function loadUserOrganizations() {
    const authStore = useAuthStore();
    if (!authStore.isLogin) {
      return [];
    }

    try {
      loading.value = true;

      // Ensure memberships are loaded (now uses RingCentral API with legacy fallback)
      await authStore.fetchUserMemberships();

      // Defensive check: Clear currentOrgId if it doesn't exist in memberships
      // This prevents cross-user contamination when different users login
      if (
        currentOrgId.value &&
        Array.isArray(memberships.value) &&
        !memberships.value.some(org => org?.orgId === currentOrgId.value)
      ) {
        console.warn('⚠️ Clearing invalid currentOrgId:', currentOrgId.value);
        currentOrgId.value = null;
      }

      // Set current org with priority: currentOrgId > lastVisitedOrgId > first org
      if (
        currentOrgId.value &&
        Array.isArray(memberships.value) &&
        memberships.value.some(org => org?.orgId === currentOrgId.value)
      ) {
        // Current persisted org is still valid, keep it
        authStore.setLastVisitedOrgId(currentOrgId.value);
      } else if (
        authStore.lastVisitedOrgId &&
        Array.isArray(memberships.value) &&
        memberships.value.some(org => org?.orgId === authStore.lastVisitedOrgId)
      ) {
        // Use lastVisitedOrgId from auth store
        currentOrgId.value = authStore.lastVisitedOrgId;
      } else if (Array.isArray(memberships.value) && memberships.value.length > 0) {
        // Otherwise set to the first organization
        currentOrgId.value = memberships.value[0].orgId;
        authStore.setLastVisitedOrgId(memberships.value[0].orgId);
      }

      // Load RingCentral connection for current org if set
      if (currentOrgId.value) {
        await loadRingCentralConnection(currentOrgId.value);
      }

      return memberships.value;
    } catch (error) {
      console.error('Failed to load organizations:', error);
      return [];
    } finally {
      loading.value = false;
    }
  }

  function switchOrganization(orgId: string) {
    const org = memberships.value.find(o => o.orgId === orgId);
    if (org) {
      currentOrgId.value = orgId;
      authStore.setLastVisitedOrgId(orgId);

      // Track last used timestamp for sorting
      lastUsedTimestamps.value[orgId] = Date.now();

      // Clear RingCentral connection when switching orgs
      ringCentralConnection.value = null;

      // Load RingCentral connection for new org
      loadRingCentralConnection(orgId);
    } else {
      console.warn('❌ Organization not found in memberships:', orgId);
    }
  }

  // RingCentral Actions
  async function loadRingCentralConnection(orgId: string) {
    if (!orgId) {return;}

    try {
      // Import RingCentral store dynamically to avoid circular dependency
      const { useRingCentralStore } = await import('./ringcentral');
      const ringCentralStore = useRingCentralStore();

      const connection = await ringCentralStore.loadConnection(orgId);
      ringCentralConnection.value = connection;
    } catch (error) {
      console.warn('Failed to load RingCentral connection for org:', orgId, error);
      ringCentralConnection.value = null;
    }
  }

  function setRingCentralConnection(connection: RingCentralConnection | null) {
    ringCentralConnection.value = connection;
  }

  function clearRingCentralConnection() {
    ringCentralConnection.value = null;
  }

  // Set business numbers for an organization
  function setOrgBusinessNumbers(orgId: string, businessNumbers: Set<string>) {
    orgBusinessNumbers.value.set(orgId, businessNumbers);
  }

  return {
    // Organization state
    currentOrgId,
    currentOrganization,
    memberships,
    hasOrganizations,
    loading,
    lastUsedTimestamps,
    currentOrgBusinessNumbers,

    // RingCentral state
    ringCentralConnection,
    isRingCentralConnected,
    ringCentralStatus,
    canManageRingCentral,

    // Organization actions
    loadUserOrganizations,
    switchOrganization,
    setOrgBusinessNumbers,

    // RingCentral actions
    loadRingCentralConnection,
    setRingCentralConnection,
    clearRingCentralConnection,
  };
});
